import { useCallback, useMemo } from 'react';
import useVariants from 'personalization-lib/hooks/variants/useVariants';
import useCrmObjectNames, { getCrmObjectKey } from 'personalization-lib/hooks/targeting/useCrmObjectNames';
import { getABMVariantName, getVariantName } from 'personalization-lib/utils/variantSelect';
import { getCrmObjectTarget, getCrmObjectTargets } from 'personalization-lib/utils/targeting';
import { STRATEGY_TYPES } from 'personalization-lib/constants/strategyType';
import { VARIANT_SELECT_MODES } from 'personalization-lib/constants/variantSelect';
import { CRM_OBJECT_TYPE_IDS } from 'personalization-lib/constants/objectTypeIds';
const useVariantSelectOptions = ({
  value,
  strategyId,
  onError,
  mode = VARIANT_SELECT_MODES.DEFAULT
}) => {
  const {
    variants,
    strategyType,
    loading: variantsLoading,
    error
  } = useVariants(strategyId, onError);
  const isAbmMode = mode === VARIANT_SELECT_MODES.ABM;
  const visibleVariants = useMemo(() => variants.filter(variant => strategyType !== STRATEGY_TYPES.ABM || variant.priority !== undefined), [variants, strategyType]);
  const crmObjectTargets = useMemo(() => isAbmMode ? getCrmObjectTargets(visibleVariants) : [], [isAbmMode, visibleVariants]);
  const {
    names: crmObjectNames,
    loading: crmObjectNamesLoading
  } = useCrmObjectNames(crmObjectTargets);
  const loading = variantsLoading || crmObjectNamesLoading;
  const getOptionText = useCallback(({
    assetData,
    targetingConfig
  }) => {
    if (!isAbmMode) {
      return getVariantName(assetData);
    }
    const crmObjectTarget = getCrmObjectTarget(targetingConfig);
    return getABMVariantName(assetData, crmObjectTarget && crmObjectNames[getCrmObjectKey(crmObjectTarget)]);
  }, [isAbmMode, crmObjectNames]);

  // In ABM mode the row is illustrated with the CRM object the variant targets, not the asset
  // preview. Only expose the target for object types CrmObjectAvatar can render (company/contact);
  // unsupported types fall back to the asset thumbnail rather than leaving the media column blank.
  const getOptionCrmObjectTarget = useCallback(({
    targetingConfig
  }) => {
    if (!isAbmMode) return undefined;
    const target = getCrmObjectTarget(targetingConfig);
    if (!target || target.objectTypeId !== CRM_OBJECT_TYPE_IDS.COMPANY && target.objectTypeId !== CRM_OBJECT_TYPE_IDS.CONTACT) {
      return undefined;
    }
    return target;
  }, [isAbmMode]);

  // Dropdown options: hide the untargeted default/all-visitors asset for ABM strategies.
  const options = useMemo(() => visibleVariants.filter(variant => variant.assetData).map(variant => Object.assign({
    value: variant.assetCoordinates.id,
    text: getOptionText(variant),
    crmObjectTarget: getOptionCrmObjectTarget(variant),
    mode,
    loading,
    error
  }, variant)), [visibleVariants, mode, loading, error, getOptionText, getOptionCrmObjectTarget]);

  // Resolve the current selection from the full list so the trigger can display the
  // base/all-visitors asset even when it is hidden from the ABM dropdown options.
  const selectedOption = useMemo(() => {
    const selected = variants.find(v => v.assetCoordinates.id === value);
    if (!(selected !== null && selected !== void 0 && selected.assetData)) return undefined;
    return Object.assign({
      value: selected.assetCoordinates.id,
      text: getOptionText(selected),
      crmObjectTarget: getOptionCrmObjectTarget(selected),
      mode,
      loading,
      error
    }, selected);
  }, [variants, value, mode, loading, error, getOptionText, getOptionCrmObjectTarget]);
  const valueNotFound = !loading && Boolean(value) && !selectedOption;
  return useMemo(() => ({
    options,
    selectedOption,
    loading,
    error,
    valueNotFound
  }), [options, selectedOption, loading, error, valueNotFound]);
};
export default useVariantSelectOptions;